import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  Empty,
  ErrorBox,
  Field,
  Money,
  Panel,
  Pill,
  Row,
  Rows,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.js';

/**
 * The payment ladder, made visible (CLAUDE.md §3).
 *
 * Everything a merchant needs when money does not reconcile itself lives here:
 * paste a forwarded ABA message (rung 2), see which transfers matched nothing,
 * and attach one to an order by hand (rung 1's safety net). Provider health is
 * shown alongside, because a degraded gateway is the reason checkout may have
 * fallen back to KHQR.
 */
export function PaymentsPage() {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [forwardText, setForwardText] = useState('');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attachTo, setAttachTo] = useState<Record<string, string>>({});

  const providers = useQuery({
    queryKey: ['providers', shopId],
    queryFn: () => api.providers(shopId!),
    enabled: Boolean(shopId),
  });

  const unmatched = useQuery({
    queryKey: ['unmatched', shopId],
    queryFn: () => api.unmatchedPayments(shopId!),
    enabled: Boolean(shopId),
  });

  const awaiting = useQuery({
    queryKey: ['orders', shopId, 'awaiting_payment', ''],
    queryFn: () => api.orders(shopId!, { status: 'awaiting_payment' }),
    enabled: Boolean(shopId),
  });

  const forward = useMutation({
    mutationFn: () => api.forwardNotification(shopId!, forwardText.trim()),
    onSuccess: (result) => {
      setError(null);
      setForwardText('');
      setOutcome(result.kind);
      void queryClient.invalidateQueries({ queryKey: ['unmatched', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', shopId] });
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const attach = useMutation({
    mutationFn: ({ notificationId, orderId }: { notificationId: string; orderId: string }) =>
      api.attachNotification(shopId!, notificationId, orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['unmatched', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', shopId] });
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <div className="flex flex-col gap-4">
      <Panel title={t('admin.payments')}>
        <Field
          label={t('merchant.paymentUnmatchedHint')}
          hint="Paste the ABA Merchant message here"
        >
          <Textarea
            className="font-mono text-xs"
            rows={3}
            value={forwardText}
            onChange={(event) => setForwardText(event.target.value)}
            placeholder="$12.65 paid by … Trx. ID: …"
          />
        </Field>
        <div className="mt-3 flex items-center gap-3">
          <Button disabled={forward.isPending || forwardText.trim().length < 20} onClick={() => forward.mutate()} >
            {forward.isPending ? t('common.loading') : t('common.confirm')}
          </Button>
          {outcome ? (
            <Pill
              tone={
                outcome === 'matched'
                  ? 'positive'
                  : outcome === 'duplicate'
                    ? 'neutral'
                    : outcome === 'unparsed'
                      ? 'danger'
                      : 'warn'
              }
            >
              {outcome}
            </Pill>
          ) : null}
        </div>
      </Panel>

      {error ? <ErrorBox message={error} /> : null}

      <Panel title={t('admin.payments')} padded={false}>
        {providers.isPending ? (
          <Spinner />
        ) : (
          <Rows>
            {providers.data?.providers.map((provider) => (
              <Row key={provider.provider}>
                <span className="flex-1 font-mono text-sm">{provider.provider}</span>
                <Pill tone={provider.enabled ? 'brand' : 'neutral'}>
                  {provider.enabled ? 'on' : 'off'}
                </Pill>
                <Pill
                  tone={
                    provider.health === 'ok' ? 'positive' : provider.health === 'down' ? 'danger' : 'warn'
                  }
                >
                  {provider.health}
                </Pill>
              </Row>
            ))}
          </Rows>
        )}
      </Panel>

      <Panel title={t('merchant.paymentUnmatched', { amount: '' })} padded={false}>
        {unmatched.isPending ? (
          <Spinner />
        ) : (unmatched.data?.notifications.length ?? 0) === 0 ? (
          <Empty title={t('common.empty')} />
        ) : (
          <Rows>
            {unmatched.data!.notifications.map((notification) => (
              <Row key={notification.id}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {notification.amount !== null && notification.currency ? (
                      <Money
                        amount={notification.amount}
                        currency={notification.currency as 'USD' | 'KHR'}
                      />
                    ) : (
                      '—'
                    )}{' '}
                    <span className="font-normal" style={{ color: 'var(--color-ink-soft)' }}>
                      {notification.payerName ?? ''}
                    </span>
                  </p>
                  <p
                    className="truncate font-mono text-[11px]"
                    style={{ color: 'var(--color-ink-faint)' }}
                  >
                    {notification.rawText}
                  </p>
                </div>

                <Select
                  className="max-w-40"
                  value={attachTo[notification.id] ?? ''}
                  onChange={(event) =>
                  setAttachTo((draft) => ({ ...draft, [notification.id]: event.target.value }))
                  }
                >
                  <option value="">—</option>
                  {awaiting.data?.items.map((order) => (
                    <option key={order.id} value={order.id}>
                      {order.code}
                    </option>
                  ))}
                </Select>

                <Button disabled={!attachTo[notification.id] || attach.isPending} onClick={() => attach.mutate({ notificationId: notification.id, orderId: attachTo[notification.id]!, }) } >
                  {t('common.confirm')}
                </Button>
              </Row>
            ))}
          </Rows>
        )}
      </Panel>
    </div>
  );
}
